import {
  Box,
  ButtonV2,
  Flex,
  IconButton,
  TextField,
} from '@ghq-abi/design-system';
import { TICKET_CONSTANTS } from '~/shared/constants';

import { useTranslate } from '@tolgee/react';
import { useUser } from '~/shared/auth/useUser';
import { useEventsAnalytics } from '~/shared/hooks/useEvaluationAnalytics';
import {
  useMyTickets,
  useMyTicketsDateFilters,
  useMyTicketsFilters,
  useMyTicketsSearch,
} from '../context';
import { FiltersIcon } from './FiltersIcon';
import { MultiSelect } from './MultiSelect';
import {
  BUSINESS_UNIT_OPTIONS,
  CATEGORY_OPTIONS,
  DATE_FORMAT_CONSTANTS,
  TICKET_STATUS_OPTIONS,
  UI_CONSTANTS,
  ZONE_OPTIONS,
  ZONE_TO_BUSINESS_UNITS_MAPPING,
} from './constants';

export function FiltersBar() {
  const { t } = useTranslate();

  const user = useUser();
  const eventsAnalytics = useEventsAnalytics();

  const { DATE } = TICKET_CONSTANTS;

  const { searchValue, setSearchValue } = useMyTicketsSearch();
  const { filters, updateFilter, clearFilters } = useMyTicketsFilters();
  const { isLoading } = useMyTickets();
  const {
    createdDateFrom,
    createdDateTo,
    showDateFilters,
    updateDateFromInput,
    updateDateToInput,
    toggleDateFilters,
  } = useMyTicketsDateFilters();

  const formatDateInput = (value: string) => {
    const cleanValue = value.replace(/\D/g, '');
    if (cleanValue.length <= DATE_FORMAT_CONSTANTS.DAY_START) {
      return cleanValue;
    }
    if (cleanValue.length <= DATE_FORMAT_CONSTANTS.MONTH_START) {
      return `${cleanValue.slice(
        0,
        DATE_FORMAT_CONSTANTS.DAY_START
      )}/${cleanValue.slice(DATE_FORMAT_CONSTANTS.DAY_START)}`;
    }
    return `${cleanValue.slice(
      0,
      DATE_FORMAT_CONSTANTS.DAY_START
    )}/${cleanValue.slice(
      DATE_FORMAT_CONSTANTS.DAY_START,
      DATE_FORMAT_CONSTANTS.MONTH_START
    )}/${cleanValue.slice(
      DATE_FORMAT_CONSTANTS.MONTH_START,
      DATE_FORMAT_CONSTANTS.YEAR_START
    )}`;
  };

  const handleDateFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatDateInput(e.target.value);
    updateDateFromInput(formatted);
  };

  const handleDateToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatDateInput(e.target.value);
    updateDateToInput(formatted);
  };

  const isDateFromValid =
    createdDateFrom.length === 0 ||
    createdDateFrom.length === DATE.FORMAT_LENGTH;
  const isDateToValid =
    createdDateTo.length === 0 || createdDateTo.length === DATE.FORMAT_LENGTH;

  const getBusinessUnitValueFromTranslationKey = (
    translationKey: string
  ): string => {
    const option = BUSINESS_UNIT_OPTIONS.find(
      opt => opt.label === translationKey
    );
    return option?.value || '';
  };

  const getFilteredBusinessUnitOptions = () => {
    if (filters.zone.length === 0) {
      return BUSINESS_UNIT_OPTIONS;
    }

    const allowedBusinessUnits = new Set<string>();

    filters.zone.forEach(zoneValue => {
      if (
        zoneValue &&
        ZONE_TO_BUSINESS_UNITS_MAPPING[
        zoneValue as keyof typeof ZONE_TO_BUSINESS_UNITS_MAPPING
        ]
      ) {
        const zoneBusinessUnitTranslationKeys =
          ZONE_TO_BUSINESS_UNITS_MAPPING[
          zoneValue as keyof typeof ZONE_TO_BUSINESS_UNITS_MAPPING
          ];
        zoneBusinessUnitTranslationKeys.forEach(translationKey => {
          const businessUnitValue =
            getBusinessUnitValueFromTranslationKey(translationKey);
          if (businessUnitValue) {
            allowedBusinessUnits.add(businessUnitValue);
          }
        });
      }
    });

    return BUSINESS_UNIT_OPTIONS.filter(
      option => option.value === '' || allowedBusinessUnits.has(option.value)
    );
  };

  const filteredBusinessUnitOptions = getFilteredBusinessUnitOptions();

  const handleZoneChange = (value: string[]) => {
    updateFilter('zone', value);

    const allowedBusinessUnits = new Set<string>();
    value.forEach(zoneValue => {
      if (
        zoneValue &&
        ZONE_TO_BUSINESS_UNITS_MAPPING[
        zoneValue as keyof typeof ZONE_TO_BUSINESS_UNITS_MAPPING
        ]
      ) {
        const zoneBusinessUnitTranslationKeys =
          ZONE_TO_BUSINESS_UNITS_MAPPING[
          zoneValue as keyof typeof ZONE_TO_BUSINESS_UNITS_MAPPING
          ];
        zoneBusinessUnitTranslationKeys.forEach(translationKey => {
          const businessUnitValue =
            getBusinessUnitValueFromTranslationKey(translationKey);
          if (businessUnitValue) {
            allowedBusinessUnits.add(businessUnitValue);
          }
        });
      }
    });

    const validBusinessUnits = filters.businessUnit.filter(
      bu => bu === '' || allowedBusinessUnits.has(bu)
    );

    if (validBusinessUnits.length !== filters.businessUnit.length) {
      updateFilter('businessUnit', validBusinessUnits);
    }
  };

  return (
    <Box
      css={{
        backgroundColor: '$white',
        border: '1px solid #CACDD5',
        borderRadius: '12px',
        padding: '$4',
        marginBottom: '$4',
      }}
    >
      <Flex align="center" css={{ width: '$full', gap: '$3' }}>
        <Box
          css={{
            width: '50%',
            minWidth: '280px',
          }}
        >
          <TextField
            placeholder={t('myTickets.searchByEmployeeOrId')}
            value={searchValue}
            onChange={e => {
              setSearchValue(e.target.value);
            }}
            containerCss={{
              width: '$full',
            }}
            css={{
              backgroundColor: '$white',
              borderRadius: '4px',
              fontSize: '$sm',
              height: '40px',
            }}
          />
        </Box>

        <Flex
          align="center"
          css={{
            flex: 1,
            gap: '$2',
            minWidth: 0,
            justifyContent: 'space-between',
          }}
        >
          <IconButton
            icon={<FiltersIcon isActive={showDateFilters} />}
            aria-label={t('myTickets.toggleDateFilters')}
            onClick={toggleDateFilters}
            css={{
              backgroundColor: '$white',
              border: '1px solid #CACDD5',
              borderRadius: '4px',
              height: '40px',
              width: '40px',
              padding: '10px',
              '& svg': {
                width: '28px',
                height: '28px',
              },
              '&:hover': {
                backgroundColor: '#D6E3EA !important',
              },
            }}
          />

          <MultiSelect
            value={filters.category}
            onValueChange={value => {
              eventsAnalytics.fireClickFilterCategoryAnalyticsEvent(user);
              updateFilter('category', value);
            }}
            options={CATEGORY_OPTIONS}
            placeholder={t('myTickets.category')}
            minWidth="140px"
          />

          <MultiSelect
            value={filters.zone}
            onValueChange={value => {
              eventsAnalytics.fireClickFilterZoneAnalyticsEvent(user);
              handleZoneChange(value);
            }}
            options={ZONE_OPTIONS}
            placeholder={t('myTickets.zone')}
            minWidth="120px"
          />

          <MultiSelect
            value={filters.status}
            onValueChange={value => {
              eventsAnalytics.fireClickFilterStatusAnalyticsEvent(user);
              updateFilter('status', value);
            }}
            options={TICKET_STATUS_OPTIONS}
            placeholder={t('myTickets.ticketStatus')}
            minWidth="160px"
          />

          <MultiSelect
            value={filters.businessUnit}
            onValueChange={value => {
              eventsAnalytics.fireClickFilterBuAnalyticsEvent(user);
              updateFilter('businessUnit', value);
            }}
            options={filteredBusinessUnitOptions}
            placeholder={t('myTickets.bu')}
            minWidth="150px"
          />
        </Flex>
      </Flex>

      {showDateFilters && (
        <Flex
          align="center"
          css={{
            marginTop: '$4',
            gap: '$3',
            backgroundColor: '#F5F6F7',
            padding: '$4',
            borderRadius: '8px',
          }}
        >
          <Box css={{ minWidth: '140px' }}>
            <Box
              css={{
                fontSize: '$xs',
                color: '$gray600',
                marginBottom: '$1',
              }}
            >
              {t('myTickets.createdDate')}
            </Box>
            <TextField
              placeholder={t('myTickets.datePlaceholder')}
              value={createdDateFrom}
              onChange={handleDateFromChange}
              onClick={() =>
                eventsAnalytics.fireClickFilterCreatedDateAnalyticsEvent(user)
              }
              maxLength={DATE.FORMAT_LENGTH}
              css={{
                backgroundColor: '$white',
                border: !isDateFromValid
                  ? '1px solid #EF4444'
                  : '1px solid $gray300',
                borderRadius: '4px',
                fontSize: '$sm',
                '&:focus': {
                  borderColor: !isDateFromValid ? '#EF4444' : '$primary',
                  boxShadow: !isDateFromValid
                    ? '0 0 0 2px rgba(239, 68, 68, 0.15)'
                    : '0 0 0 2px rgba(50, 90, 109, 0.15)',
                },
              }}
            />
          </Box>

          <Box css={{ minWidth: '140px' }}>
            <Box
              css={{
                fontSize: '$xs',
                color: '$gray600',
                marginBottom: '$1',
              }}
            >
              {t('myTickets.endDate')}
            </Box>
            <TextField
              placeholder={t('myTickets.datePlaceholder')}
              value={createdDateTo}
              onChange={handleDateToChange}
              onClick={() =>
                eventsAnalytics.fireClickFilterEndDateAnalyticsEvent(user)
              }
              maxLength={DATE.FORMAT_LENGTH}
              css={{
                backgroundColor: '$white',
                border: !isDateToValid
                  ? '1px solid #EF4444'
                  : '1px solid $gray300',
                borderRadius: '4px',
                fontSize: '$sm',
                '&:focus': {
                  borderColor: !isDateToValid ? '#EF4444' : '$primary',
                  boxShadow: !isDateToValid
                    ? '0 0 0 2px rgba(239, 68, 68, 0.15)'
                    : '0 0 0 2px rgba(50, 90, 109, 0.15)',
                },
              }}
            />
          </Box>

          <Box
            css={{
              minWidth: '120px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              css={{
                fontSize: '$xs',
                color: 'transparent',
                marginBottom: '$1',
                height: '16px',
              }}
            >
              &nbsp;
            </Box>
            <ButtonV2
              variant="secondary"
              size="md"
              onClick={clearFilters}
              disabled={isLoading}
              css={{
                backgroundColor: '$white',
                color: '#325A6D',
                border: '1px solid #E5E7EB',
                borderRadius: '4px',
                fontSize: '$sm',
                fontWeight: '$medium',
                padding: '$2 $4',
                minWidth: '120px',
                height: '42px',
                opacity: isLoading ? UI_CONSTANTS.LOADING_OPACITY : 1,
                cursor: isLoading ? 'not-allowed' : 'pointer',
                '&:hover': {
                  backgroundColor: isLoading ? '$white' : '#B8D4E3 !important',
                  color: '#325A6D !important',
                },
              }}
            >
              {isLoading
                ? t('myTickets.clearing')
                : t('myTickets.clearFilters')}
            </ButtonV2>
          </Box>
        </Flex>
      )}
    </Box>
  );
}
